/**
 * @file Xoe agent SSE transport — POST a turn, stream the progress back (P3.3).
 * @module api/xoe/XoeStreamApi
 */

import { ENV } from '../../config/runtimeEnv';
import type { XoeStreamEvent } from '../../types/xoe/XoeStreamTypes';
import type { XoeTurnContext } from '../../types/xoe/XoeTypes';
import { createSseParser } from '../../utils/xoe/parseSseStream';
import { httpFetch } from '../../lib/http/fetch';

/** Event names this client handles. Anything else on the wire is ignored — see below. */
const HANDLED_EVENTS = new Set<string>([
	'turn.started',
	'tool.requested',
	'tool.completed',
	'turn.capped',
	'reasoning.block',
	'turn.completed',
	'turn.error',
]);

/**
 * A transport-level failure, carrying the HTTP status so the caller can distinguish
 * "not deployed" from "not permitted" from "rejected".
 */
export class XoeStreamError extends Error {
	readonly status: number;
	readonly userMessage: string;

	constructor(status: number) {
		super(`Xoe stream request failed with status ${status}`);
		this.name = 'XoeStreamError';
		this.status = status;
		this.userMessage = userMessageForStatus(status);
	}
}

const userMessageForStatus = (status: number): string => {
	switch (status) {
		case 503:
			return 'Xoe streaming is not available in this deployment yet.';
		case 401:
		case 403:
			return 'You do not have permission to use Xoe streaming.';
		case 422:
			return 'That message could not be sent. Please shorten it and try again.';
		default:
			return 'Xoe could not be reached. Please try again.';
	}
};

export interface StreamXoeMessageParams {
	uuid: string;
	text: string;
	context?: XoeTurnContext;
	/** Called for each recognised frame, in wire order. */
	onEvent: (event: XoeStreamEvent) => void;
	/** From an AbortController — aborting closes the stream, which cancels the loop. */
	signal?: AbortSignal;
}

/**
 * Send one practitioner message and stream the turn back.
 */
export const streamXoeMessage = async ({
	uuid,
	text,
	context,
	onEvent,
	signal,
}: StreamXoeMessageParams): Promise<void> => {
	const baseUrl = ENV.VITE_XOE_AGENT_SERVER;

	const body: { text: string; context?: XoeTurnContext } = context ? { text, context } : { text };

	let response: Response;
	try {
		response = (await httpFetch(
			`${baseUrl}/practitioner/xoe/conversations/${uuid}/messages/stream`,
			{
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					Accept: 'text/event-stream',
				},
				body: JSON.stringify(body),
				signal,
			},
		)) as Response;
	} catch (err) {
		if (isAbortError(err)) return;
		throw err;
	}

	// Before the first byte — see the module docstring.
	if (!response.ok) {
		throw new XoeStreamError(response.status);
	}

	if (!response.body) {
		// A 200 with no body would otherwise present as a turn that never completes and
		// a composer locked forever. Fail loudly instead.
		throw new XoeStreamError(response.status || 500);
	}

	const reader = response.body.getReader();
	const decoder = new TextDecoder();
	const parse = createSseParser();

	try {
		for (;;) {
			const { done, value } = await reader.read();
			if (done) break;

			// `stream: true` is load-bearing: a multi-byte UTF-8 character split across
			// two chunks decodes to a replacement character without it.
			for (const frame of parse(decoder.decode(value, { stream: true }))) {
				// FORWARD COMPATIBILITY. An unrecognised event name is dropped, never
				// thrown — the server stamps `"v": 1` on every payload precisely so it
				// can add an event without a coordinated frontend release.
				if (!HANDLED_EVENTS.has(frame.event)) continue;

				onEvent({ event: frame.event, ...frame.data } as unknown as XoeStreamEvent);
			}
		}
	} catch (err) {
		if (isAbortError(err)) return;
		throw err;
	} finally {
		// Releases the connection when the consumer stops early.
		void reader.cancel?.().catch(() => undefined);
	}
};

/** True for a fetch/reader abort, however the runtime spells it. */
const isAbortError = (err: unknown): boolean =>
	typeof err === 'object' &&
	err !== null &&
	(err as { name?: string }).name === 'AbortError';
